import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'reactstrap';
import './style.scss';

const PERMISSION_OPTIONS = [
  { value: 'MEMBER', label: 'Member' },
  { value: 'HOST', label: 'Host' },
  { value: 'ADMIN', label: 'Admin' },
];

function ModalInvite({ isOpen, toggle, onInvite = null, canManage }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setRole(PERMISSION_OPTIONS[0]?.value);
      setMessage('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handleRoleChange = useCallback((e) => {
    setRole(e.target.value);
  }, []);

  const handleMessageChange = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!onInvite || isSubmitting) return;

      setIsSubmitting(true);
      try {
        await onInvite({ email, role, message }, () => {
          toggle();
        });
      } catch (error) {
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, role, message, onInvite, isSubmitting, toggle]
  );

  if (!canManage) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="modal-invite-wrapper"
      fade
    >
      <div className="modal-invite-header">
        <h5 className="modal-invite-title">Invite Member</h5>
        <button
          type="button"
          className="modal-invite-close"
          onClick={toggle}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="modal-invite-body">
          <div className="form-field">
            <label className="field-label" htmlFor="invite-email">
              Email Address
            </label>
            <input
              id="invite-email"
              type="email"
              className="field-input"
              placeholder="member@example.com"
              value={email}
              onChange={handleEmailChange}
              required
            />
          </div>

          <div className="form-field">
            <label className="field-label" htmlFor="invite-role">
              Permission Level
            </label>
            <select
              id="invite-role"
              className="field-select"
              value={role}
              onChange={handleRoleChange}
            >
              {PERMISSION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label className="field-label" htmlFor="invite-message">
              Personal Message (optional)
            </label>
            <textarea
              id="invite-message"
              className="field-textarea"
              placeholder="Add a personal note to your invitation..."
              value={message}
              onChange={handleMessageChange}
            />
          </div>
        </div>

        <div className="modal-invite-footer">
          <button
            type="button"
            className="btn-cancel"
            onClick={toggle}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-send"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

ModalInvite.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onInvite: PropTypes.func,
  canManage: PropTypes.bool.isRequired,
};

export default memo(ModalInvite);
